import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { approvalFormTableStyles } from '@/components/Approval/@components/ApprovalFormTable/styles';
import { getApprovalItemsUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';

export type ApprovalItemStatus = {
  itemId: string;
  label?: string | null;
  status?: string | null;
};

/**
 * 저장 카드 HTML 에서 결재 건 ID → 표시 제목. 2차 양식(136·156·166·184·186)은 상태 API label 이 결재 건 ID 라 제목으로 바꿔 보여줌.
 * pairs: [ID 속성, 제목 속성] — V2 카드·레거시 버튼 속성이 양식마다 다름
 */
export const readItemLabels = (
  content: string | null,
  pairs: [string, string][],
) => {
  const map = new Map<string, string>();
  if (!content || typeof DOMParser === 'undefined') return map;
  const doc = new DOMParser().parseFromString(content, 'text/html');
  pairs.forEach(([idAttr, labelAttr]) =>
    doc.querySelectorAll(`[${idAttr}][${labelAttr}]`).forEach((el) => {
      const id = el.getAttribute(idAttr) ?? '';
      const label = el.getAttribute(labelAttr) ?? '';
      if (id && label && !map.has(id)) map.set(id, label);
    }),
  );
  return map;
};

/** 레거시 GW_STS_CD 문구 — 그 밖의 상태는 빈칸 */
const STATUS_LABEL: Partial<Record<string, string>> = {
  EXCLUDED: '부분반려',
  PROCESSING: '결재중',
  COMPLETED: '완료',
  REJECTED: '반려',
};

type Props = {
  documentId: string;
  buttonLabel?: string;
  title?: string;
  /** 첫 열 제목 — 2차 양식 레거시는 'No' */
  labelHeader?: string;
  /** 결재 건 ID → 표시 제목(readItemLabels) */
  labels?: Map<string, string>;
  /** 빨간 글씨 상태 — 레거시 양식마다 다름(123 은 반려도 빨강) */
  dangerStatuses?: string[];
  /** 레거시에 문구가 있는 양식만. 없으면 조회 중 스피너, 없음은 빈 창, 실패는 공통 안내 */
  messages?: { loading?: string; empty?: string; error?: string };
  /** 2차 양식(136·156·166·184·186) — 상태를 포탈이 기록해 V1 문서는 422(기록 없음) */
  portalRecordedStatus?: boolean;
};

/**
 * GERP 결재 마스터 계열 조회 화면 「추가된 품번 확인」 — 문서에 묶인 품번과 GERP 현재 결재상태.
 * 창을 열 때마다 다시 조회
 */
const ApprovalItemsStatus = ({
  documentId,
  buttonLabel = '추가된 품번 확인',
  title = '품번확인',
  labelHeader = '품번',
  labels,
  dangerStatuses = ['EXCLUDED'],
  messages,
  portalRecordedStatus = false,
}: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<ModalRef>(null);

  const { data, error, isLoading } = useSWR<ApprovalItemStatus[]>(
    isOpen ? getApprovalItemsUrl(documentId) : null,
    getNestedDataFetcher,
    // GERP 를 거쳐 오므로 실패해도 자동 재시도 안 함
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const open = () => {
    setIsOpen(true);
    modalRef.current?.open();
  };

  const close = () => {
    setIsOpen(false);
    modalRef.current?.close();
  };

  const renderBody = () => {
    if (
      portalRecordedStatus &&
      axios.isAxiosError(error) &&
      error.response?.status === 422
    )
      return <Message>{t('확인할 수 있는 결재 건 상태가 없습니다.')}</Message>;
    if (error)
      return (
        <Message className="is-danger">
          {t(messages?.error ?? '데이터를 불러오지 못했습니다.')}
        </Message>
      );
    if (isLoading || !data)
      return messages?.loading ? (
        <Message>{t(messages.loading)}</Message>
      ) : (
        <Message>
          <Spinner theme="black" size="24px" />
        </Message>
      );
    if (!data.length)
      return <Message>{messages?.empty ? t(messages.empty) : null}</Message>;
    return (
      <ItemsTable>
        <colgroup>
          <col width="50%" />
          <col width="50%" />
        </colgroup>
        <thead>
          <tr>
            <th>{t(labelHeader)}</th>
            <th>{t('결재상태')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const label = STATUS_LABEL[item.status ?? ''];
            return (
              <tr key={item.itemId}>
                <td>{labels?.get(item.itemId) || item.label || item.itemId}</td>
                <td
                  className={
                    dangerStatuses.includes(item.status ?? '')
                      ? 'is-danger'
                      : undefined
                  }
                >
                  {label ? t(label) : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </ItemsTable>
    );
  };

  return (
    <>
      <TopActions>
        <BoxButton
          type="button"
          variant="secondary"
          size="small"
          onClick={open}
        >
          {t(buttonLabel)}
        </BoxButton>
      </TopActions>
      <Modal ref={modalRef} title={t(title)} size="x-small" handleClose={close}>
        {renderBody()}
      </Modal>
    </>
  );
};

const TopActions = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: 8,
});

const ItemsTable = styled('table')(({ theme }) => ({
  ...approvalFormTableStyles(theme),
  [theme.breakpoints.down('md')]: { minWidth: 0 },
  '& td.is-danger': { color: 'var(--dk-danger, #F55060)' },
}));

const Message = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  padding: '24px 0',
  '&.is-danger': { color: 'var(--dk-danger, #F55060)' },
});

export default ApprovalItemsStatus;
